/**
 * Strumento — pagina di dettaglio a schermo intero (mini-IDE, ADR-0033 S4).
 *
 * `/ai/tools/new` (create) e `/ai/tools/:id` (edit) usano lo STESSO editor: header di
 * identità (nome · chiave · linguaggio · attivo · Salva · Elimina), editor Monaco a
 * piena altezza al centro, e una rail destra con lo schema DERIVATO (read-only),
 * descrizione, librerie, file di output, timeout e il pannello di test-run.
 *
 * Un tool è una FUNZIONE che ritorna un valore — Python (`@tool def …`) o TypeScript
 * (`function handler(input): Output`, transpilato FE→JS e girato sull'immagine node del
 * sandbox). Lo schema + la descrizione sono DERIVATI dal codice, mai scritti a mano.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BookOpen, Download, Play, Trash2, Wrench } from "lucide-react";

import { usePermissions } from "@/lib/permissions";
import { CodeEditor } from "@/components/canonical/CodeEditor";
import { ToolDocsDialog } from "@/components/ai/ToolDocsDialog";
import { compileToolTs } from "@/lib/appLogicTs";
import { DOCS_LABELS, pickToolLang } from "@/lib/toolDocs";
import { type AiTool, type AiToolCreate, type AiToolRunResult, toolsApi } from "@/lib/api-ai";

const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm";
const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";

const LIST_KEY = ["ai", "tools"] as const;

//: Starter code per language — a function that RETURNS a value (no JSON boilerplate).
const PY_SCAFFOLD =
  '@tool\ndef my_tool(text: str) -> dict:\n    """Descrivi cosa fa lo strumento."""\n    return {"ok": True}\n';
const TS_SCAFFOLD =
  "interface Input {\n  text: string;\n}\n\ninterface Output {\n  ok: boolean;\n}\n\n/** Descrivi cosa fa lo strumento. */\nfunction handler(input: Input): Output {\n  return { ok: true };\n}\n";

//: Ambient node globals for the TS editor — a tool's transpiled JS runs on the sidecar's
//: node image (CommonJS), so `require`/`Buffer`/`process` exist at runtime; declaring them
//: here silences Monaco's "Cannot find name 'require'". `TrayFile` is the file-input type.
const TOOL_TS_DTS = [
  "declare function require(name: string): any;",
  "declare const Buffer: { from(data: any, enc?: string): any; alloc(size: number): any; concat(list: any[]): any };",
  "declare const process: { env: Record<string, string | undefined>; argv: string[]; exit(code?: number): void };",
  "declare const module: { exports: any };",
  "declare const exports: any;",
  "declare function setTimeout(fn: (...a: any[]) => void, ms?: number): any;",
  "/** A binary file input: the console uploads a file; the handler receives its bytes. */",
  "type TrayFile = Uint8Array;",
].join("\n");

function fileParamsOf(schema: Record<string, unknown>): string[] {
  const props = (schema?.properties ?? {}) as Record<string, unknown>;
  if (typeof props !== "object" || props === null) return [];
  return Object.entries(props)
    .filter(([, s]) => s && typeof s === "object" && (s as Record<string, unknown>)["x-tray-file"])
    .map(([k]) => k);
}

// --- route wrapper: load (edit) or start blank (create) ---------------------

export function ToolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const creating = !id || id === "new";
  const q = useQuery({
    queryKey: ["ai", "tools", id],
    queryFn: () => toolsApi.get(id!),
    enabled: !creating,
  });

  if (creating) return <ToolEditor tool={null} />;
  if (q.isLoading) return <p className="p-6 text-sm text-muted-foreground">Caricamento…</p>;
  if (!q.data) return <p className="p-6 text-sm text-danger">Strumento non trovato.</p>;
  // Keyed so the form's useState initialisers see the loaded tool (re-mount per tool).
  return <ToolEditor key={q.data.id} tool={q.data} />;
}

// --- the editor (create + edit) ---------------------------------------------

function ToolEditor({ tool }: { tool: AiTool | null }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const { i18n } = useTranslation();
  const docsLabel = DOCS_LABELS[pickToolLang(i18n.language)].button;
  const canWrite = can("tools", "write");
  const canDelete = can("tools", "delete");
  const [docsOpen, setDocsOpen] = useState(false);

  const tsAvailable = true; // both languages are always offered (the toggle mirrors this)

  const [savedId, setSavedId] = useState<string | null>(tool?.id ?? null);
  const [key, setKey] = useState(tool?.key ?? "");
  const [name, setName] = useState(tool?.name ?? "");
  const [description, setDescription] = useState(tool?.description ?? "");
  const [language, setLanguage] = useState<string>(tool?.language ?? "python");
  const isTs = language === "javascript";
  const [code, setCode] = useState(
    tool ? (tool.language === "javascript" ? (tool.code_ts ?? "") : tool.code) : PY_SCAFFOLD,
  );
  const [outputFiles, setOutputFiles] = useState((tool?.output_files ?? []).join(", "));
  const [timeout, setTimeout] = useState(tool?.timeout_seconds ?? 60);
  const [enabled, setEnabled] = useState(tool?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);
  const [savedSchema, setSavedSchema] = useState<Record<string, unknown>>(tool?.input_schema ?? {});
  const [tsDerived, setTsDerived] = useState<{ schema: Record<string, unknown>; errors: string[] }>({
    schema: {},
    errors: [],
  });

  function switchLanguage(lang: string) {
    setLanguage(lang);
    if (!savedId) setCode(lang === "javascript" ? TS_SCAFFOLD : PY_SCAFFOLD);
  }

  useEffect(() => {
    if (!isTs) return;
    let alive = true;
    const t = window.setTimeout(() => {
      void compileToolTs(code).then((r) => {
        if (alive) setTsDerived({ schema: r.inputSchema, errors: r.diagnostics.map((d) => d.message) });
      });
    }, 300);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [code, isTs]);

  const save = useMutation({
    mutationFn: async () => {
      const csv = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);
      // No `libraries`: the sandbox fat-image pre-installs the whole allow-list, so a tool
      // just `require`s/imports what it needs — declaring them is redundant (the rail shows
      // a read-only reference of what's available).
      const base = {
        name,
        output_files: csv(outputFiles),
        timeout_seconds: Number(timeout) || 60,
        enabled,
      };
      let body: Omit<AiToolCreate, "key">;
      if (isTs) {
        const r = await compileToolTs(code);
        if (!r.ok) throw new Error(`Errore TypeScript: ${r.diagnostics.map((d) => d.message).join("; ")}`);
        body = {
          ...base,
          language: "javascript",
          code: r.codeJs,
          code_ts: code,
          input_schema: r.inputSchema,
          description: r.description ?? description ?? null,
        };
      } else {
        body = { ...base, language: "python", code, description: description || null };
      }
      return savedId ? toolsApi.update(savedId, body) : toolsApi.create({ key, ...body });
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: LIST_KEY });
      setSavedSchema(saved.input_schema);
      setDescription(saved.description ?? "");
      if (!savedId) {
        setSavedId(saved.id);
        navigate(`/ai/tools/${saved.id}`, { replace: true }); // create → become an edit URL
      }
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Errore"),
  });

  const remove = useMutation({
    mutationFn: () => toolsApi.remove(savedId!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LIST_KEY });
      navigate("/ai/tools");
    },
  });

  const derivedSchema = isTs ? tsDerived.schema : savedSchema;
  const fileParams = fileParamsOf(derivedSchema);

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      {/* Identity header */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <button type="button" className={`${btnCls} px-2`} onClick={() => navigate("/ai/tools")} title="Torna agli strumenti">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Wrench className="h-4 w-4 text-muted-foreground" />
        <input
          className={`${inputCls} w-56`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome strumento"
        />
        <input
          className={`${inputCls} w-44 font-mono`}
          value={key}
          disabled={!!savedId}
          onChange={(e) => setKey(e.target.value)}
          placeholder="chiave_funzione"
          title="Nome funzione visto dal modello (immutabile)"
        />
        <div className="inline-flex rounded-md border border-border p-0.5">
          <button type="button"
                  className={`rounded px-2.5 py-1 text-sm ${!isTs ? "bg-muted font-medium" : "text-muted-foreground"}`}
                  disabled={!!savedId && isTs}
                  onClick={() => switchLanguage("python")}>
            Python
          </button>
          {tsAvailable && (
            <button type="button"
                    className={`rounded px-2.5 py-1 text-sm ${isTs ? "bg-muted font-medium" : "text-muted-foreground"}`}
                    disabled={!!savedId && !isTs}
                    onClick={() => switchLanguage("javascript")}>
              TypeScript
            </button>
          )}
        </div>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Attivo
        </label>
        <div className="ml-auto flex items-center gap-2">
          {savedId && canDelete && (
            <button
              type="button"
              className={`${btnCls} text-danger`}
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Eliminare lo strumento “${name}”?`)) remove.mutate();
              }}
            >
              <Trash2 className="h-4 w-4" /> Elimina
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className={`${btnCls} bg-primary text-primary-foreground hover:bg-primary/90`}
              disabled={save.isPending || !name.trim() || (!savedId && !key.trim()) || !code.trim()}
              onClick={() => { setError(null); save.mutate(); }}
            >
              {savedId ? "Salva" : "Crea"}
            </button>
          )}
        </div>
      </div>

      {error && <p className="shrink-0 border-b border-danger/40 bg-danger/5 px-4 py-1.5 text-sm text-danger">{error}</p>}

      {/* Body: editor (center) + rail (right) */}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 flex-1 flex-col border-r border-border">
          <div className="flex-1 min-h-0">
            <CodeEditor
              value={code}
              onChange={setCode}
              language={isTs ? "typescript" : "python"}
              jsAmbientTypes={isTs ? TOOL_TS_DTS : undefined}
              readOnly={!canWrite}
              height="100%"
            />
          </div>
          {isTs && tsDerived.errors.length > 0 && (
            <p className="shrink-0 border-t border-border px-3 py-1.5 text-xs text-danger">
              {tsDerived.errors.join(" · ")}
            </p>
          )}
        </div>

        <aside className="w-96 shrink-0 space-y-4 overflow-y-auto p-3">
          <label className="block">
            <span className="text-xs text-muted-foreground">Descrizione — auto dal docstring/JSDoc; altrimenti questa</span>
            <input className={`${inputCls} mt-1 w-full`} value={description}
                   onChange={(e) => setDescription(e.target.value)} />
          </label>

          <div>
            <span className="text-xs text-muted-foreground">Parametri (derivati dal codice)</span>
            <div className="mt-1">
              <DerivedSchema
                schema={derivedSchema}
                note={
                  isTs
                    ? "Nessun parametro — dichiara un tipo Input."
                    : savedId
                      ? "Nessun parametro."
                      : "Verrà derivato dal codice al salvataggio."
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-muted-foreground">File di output (separati da virgola)</span>
              <input className={`${inputCls} mt-1 w-full`} value={outputFiles}
                     onChange={(e) => setOutputFiles(e.target.value)} placeholder="slides.pptx" />
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground">Timeout (s)</span>
              <input type="number" className={`${inputCls} mt-1 w-full`} value={timeout}
                     onChange={(e) => setTimeout(Number(e.target.value))} />
            </label>
          </div>

          <button type="button" className={`${btnCls} w-full justify-center`} onClick={() => setDocsOpen(true)}>
            <BookOpen className="h-4 w-4" /> {docsLabel}
          </button>

          {savedId && canWrite && <TestRunPanel toolId={savedId} fileParams={fileParams} />}
        </aside>
      </div>

      {docsOpen && <ToolDocsDialog language={language} onClose={() => setDocsOpen(false)} />}
    </div>
  );
}

// --- shared sub-components ---------------------------------------------------

function DerivedSchema({ schema, note }: { schema: Record<string, unknown>; note?: string }) {
  const props = (schema?.properties ?? {}) as Record<string, Record<string, unknown>>;
  const required = new Set((schema?.required as string[]) ?? []);
  const entries = Object.entries(props);
  return (
    <div className="rounded-md border border-border bg-muted/20 p-2 text-xs">
      {entries.length === 0 ? (
        <span className="text-muted-foreground">{note ?? "Nessun parametro."}</span>
      ) : (
        <ul className="space-y-1">
          {entries.map(([name, s]) => {
            const isFile = !!s["x-tray-file"];
            return (
              <li key={name} className="flex items-center gap-2 font-mono">
                <span className="font-medium">{name}</span>
                <span className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
                  {isFile ? "file" : String(s.type ?? "any")}
                </span>
                {required.has(name) && <span className="text-[10px] text-danger">obbligatorio</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TestRunPanel({ toolId, fileParams }: { toolId: string; fileParams: string[] }) {
  const [argsText, setArgsText] = useState("{}");
  const [files, setFiles] = useState<Record<string, File>>({});
  const [result, setResult] = useState<AiToolRunResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => {
      let args: Record<string, unknown> = {};
      try {
        args = argsText.trim() ? JSON.parse(argsText) : {};
      } catch {
        throw new Error("Argomenti JSON non validi");
      }
      for (const [param, f] of Object.entries(files)) args[param] = f.name;
      return toolsApi.run(toolId, args, Object.values(files));
    },
    onSuccess: (r) => { setResult(r); setErr(null); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Errore"),
  });

  return (
    <div className="border-t border-border pt-3">
      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        <Play className="h-3.5 w-3.5" /> Prova
      </h4>
      <label className="block">
        <span className="text-xs text-muted-foreground">Argomenti (JSON)</span>
        <textarea className={`${inputCls} mt-1 h-20 w-full font-mono text-xs`} value={argsText}
                  onChange={(e) => setArgsText(e.target.value)} spellCheck={false} />
      </label>
      {fileParams.map((p) => (
        <label key={p} className="mt-2 block text-xs text-muted-foreground">
          File “{p}”
          <input
            type="file"
            className="mt-1 block w-full text-xs"
            onChange={(e) => {
              const f = e.target.files?.[0];
              setFiles((prev) => (f ? { ...prev, [p]: f } : prev));
            }}
          />
        </label>
      ))}
      <button
        type="button"
        className={`${btnCls} mt-3`}
        disabled={run.isPending}
        onClick={() => { setResult(null); run.mutate(); }}
      >
        <Play className="h-3.5 w-3.5" /> {run.isPending ? "Esecuzione…" : "Esegui"}
      </button>

      {err && <p className="mt-2 text-sm text-danger">{err}</p>}
      {result && (
        <div className="mt-3 space-y-2 text-xs">
          {result.ok ? (
            <>
              <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted/40 p-2">
                {JSON.stringify(result.output, null, 2)}
              </pre>
              {result.files.map((f) => (
                <button
                  key={f.ref}
                  type="button"
                  className={btnCls}
                  onClick={() => void toolsApi.downloadOutput(f.ref, f.name)}
                >
                  <Download className="h-3.5 w-3.5" /> {f.name}
                </button>
              ))}
            </>
          ) : (
            <div className="rounded-md border border-danger/40 bg-danger/5 p-2 text-danger">
              <p className="font-medium">Errore: {result.error}</p>
              {result.stderr && <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap">{result.stderr}</pre>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
